import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../utils/api";

const asArray = (value) => (Array.isArray(value) ? value : []);

const tone = (value) => {
  const key = String(value || "checking").toLowerCase();
  if (key.includes("red") || key.includes("critical") || key.includes("closed")) return "red";
  if (key.includes("amber") || key.includes("busy") || key.includes("delay")) return "amber";
  if (key.includes("green") || key.includes("open") || key.includes("normal")) return "green";
  return "checking";
};

const formatClock = (value) => {
  const text = String(value || "").trim();
  if (/^\d{2}:\d{2}/.test(text)) return text.slice(0, 5);
  return text;
};

const compactText = (value, fallback) => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return fallback;
  return text.length > 82 ? `${text.slice(0, 79).trim()}...` : text;
};

const roadSummary = (road = {}) => {
  const corridors = asArray(road.corridors);
  if (!corridors.length) return { status: "Checking", detail: "LEGOLAND road corridors are updating" };
  const ranked = corridors.slice().sort((a, b) => Number(b.route_delta_sec || 0) - Number(a.route_delta_sec || 0));
  const current = ranked[0] || {};
  const delta = Math.round(Number(current.route_delta_sec || 0) / 60);
  return {
    status: String(current.status_label || "Live").replace(/_/g, " "),
    detail: delta >= 2
      ? `${current.display_name || "Main approach"} • allow about ${delta} extra mins`
      : `${current.display_name || "Main approach"} • broadly on usual time`,
  };
};

const railSummary = (rail = {}) => {
  const board = asArray(rail.boards)[0] || {};
  return {
    status: String(board?.board?.status_label || board?.target?.effective_status_label || "Checking").replace(/_/g, " "),
    detail: compactText(
      board?.board?.reason_text || board?.reason_text,
      "Slough and Windsor rail connections are updating"
    ),
  };
};

const LegolandMinibusLiveSummary = () => {
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await api.get("/api/open/legoland/live-summary");
        if (!cancelled) setState({ loading: false, data: response?.data || null });
      } catch (error) {
        if (!cancelled) setState({ loading: false, data: null });
      }
    };
    load();
    const timer = window.setInterval(load, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  const cards = useMemo(() => {
    const data = state.data || {};
    const parkDay = data?.park?.day || null;
    const weather = data?.park?.weather || null;
    const road = roadSummary(data.road);
    const rail = railSummary(data.rail);
    const parkOpen = Boolean(parkDay?.is_open);
    const hours = parkDay?.open_time && parkDay?.close_time
      ? `${formatClock(parkDay.open_time)}–${formatClock(parkDay.close_time)}`
      : "Hours updating";

    return [
      {
        icon: "🎢",
        label: "Park today",
        value: state.loading ? "Checking" : parkDay ? (parkOpen ? "Open" : "Closed") : "Checking",
        detail: parkDay ? (parkOpen ? hours : "Check another visit date") : "Calendar status updating",
        tone: state.loading ? "checking" : tone(parkOpen ? "open" : parkDay ? "closed" : "checking"),
      },
      {
        icon: "🚦",
        label: "Roads",
        value: state.loading ? "Checking" : road.status,
        detail: state.loading ? "Live corridors updating" : road.detail,
        tone: tone(road.status),
      },
      {
        icon: "🚆",
        label: "Rail connections",
        value: state.loading ? "Checking" : rail.status,
        detail: state.loading ? "Rail status updating" : rail.detail,
        tone: tone(rail.status),
      },
      {
        icon: "🌦️",
        label: "Weather",
        value: state.loading ? "Checking" : weather?.temp_c != null ? `${weather.temp_c}°C` : "Checking",
        detail: compactText(weather?.weather_text, "Park weather updating"),
        tone: "checking",
      },
    ];
  }, [state]);

  return (
    <section className="legoland-minibus-live" aria-label="Live LEGOLAND journey checks">
      <header>
        <div>
          <span>Live LEGOLAND context</span>
          <h2>Plan the group journey around the park day</h2>
          <p>Compact road, rail, weather and opening-status checks from our full LEGOLAND planner.</p>
        </div>
        <Link to="/legoland-budget-planner">Open full budget planner <b>→</b></Link>
      </header>
      <div className="legoland-minibus-live-grid">
        {cards.map((card) => (
          <article key={card.label} data-tone={card.tone}>
            <b aria-hidden="true">{card.icon}</b>
            <div>
              <span>{card.label}</span>
              <strong>{card.value}</strong>
              <small>{card.detail}</small>
            </div>
          </article>
        ))}
      </div>
      <p className="legoland-minibus-live-note">Live information supports planning; your pickup time and fixed minibus quote are confirmed separately.</p>
    </section>
  );
};

export default LegolandMinibusLiveSummary;
